<template>
  <section id="shopAssembly">
    <div id="breadcrumb">
      <div class="container">
        <ul>
          <li><router-link to="/" tag="a">Home</router-link></li>
          <li>
            <router-link to="/rfq/create" tag="a">Create New Quote</router-link>
          </li>
          <li>
            <router-link to="/rfq/chooseassembly" tag="a"
              >Choose Assembly</router-link
            >
          </li>
          <li class="last">Choose Part</li>
        </ul>
      </div>
    </div>

    <b-form id="assemblyForm" @submit.prevent="">
      <div id="assemblyList">
        <div class="container assembly-body-wrap">
          <div class="row">
            <div class="col-12 assembly-results-col">
              <!-- Diagram banner — click opens the assembly
                   diagram in a modal. Sits at the top of the
                   parts column as a visual header. -->
              <button type="button"
                      class="diagram-banner"
                      data-toggle="modal"
                      data-target="#imagemodal"
                      title="View assembly diagram"
                      @click="resetImageZoom">
                <span class="diagram-banner-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg"
                       viewBox="0 0 24 24"
                       width="18" height="18"
                       fill="none"
                       stroke="currentColor"
                       stroke-width="2"
                       stroke-linecap="round"
                       stroke-linejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                </span>
                <span class="diagram-banner-label">View Assembly Diagram</span>
                <span class="diagram-banner-cta">Open</span>
              </button>

              <div class="modal fade"
                   id="imagemodal"
                   tabindex="-1"
                   role="dialog"
                   aria-labelledby="imagemodalLabel"
                   aria-hidden="true">
                <div class="modal-dialog modal-lg modal-image-zoom">
                  <div class="modal-content">
                    <div class="modal-header image-zoom-header">
                      <h5 class="modal-title" id="imagemodalLabel">
                        {{ machineDescription || 'Assembly Diagram' }}
                      </h5>
                      <button type="button"
                              class="close"
                              data-dismiss="modal"
                              @click="resetImageZoom"
                              aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                    <div class="modal-body image-zoom-body">
                      <div class="image-zoom-canvas">
                        <img :src="image || require('../../../assets/img/jc-steele-placehold.jpg')"
                             alt="Assembly diagram"
                             class="image-zoom-img"
                             :style="{ transform: 'scale(' + imageZoom + ')' }" />
                      </div>
                    </div>
                    <div class="modal-footer image-zoom-footer">
                      <button type="button"
                              class="image-zoom-btn"
                              :disabled="imageZoom <= 0.5"
                              @click="zoomOut"
                              title="Zoom out">−</button>
                      <button type="button"
                              class="image-zoom-btn"
                              :disabled="imageZoom >= 3"
                              @click="zoomIn"
                              title="Zoom in">+</button>
                      <button type="button"
                              class="image-zoom-btn"
                              @click="resetImageZoom"
                              title="Reset zoom">RESET</button>
                      <button type="button"
                              class="image-zoom-btn image-zoom-btn-close"
                              data-dismiss="modal"
                              @click="resetImageZoom">CLOSE</button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="alert alert-danger"
                   v-if="!isSubAssemblyLoading && subAssemblies.length <= 0">
                Sorry, there are no sub-assemblies for the selected assembly at
                this time.
              </div>
              <div class="assembly-card"
                   v-for="assemblies in subAssemblies"
                   :key="assemblies.value"
                   :data-id="assemblies.value">
            <button type="button"
                    class="assembly-card-header"
                    :class="{ 'is-open': isAssemblyExpanded(assemblies.value) }"
                    @click="toggleAssembly(assemblies.value)"
                    :aria-expanded="isAssemblyExpanded(assemblies.value) ? 'true' : 'false'">
              <span class="assembly-card-chev">
                {{ isAssemblyExpanded(assemblies.value) ? '−' : '+' }}
              </span>
              <span class="assembly-card-titles">
                <span class="assembly-card-name">{{ assemblies.text }}</span>
              </span>
              <span class="assembly-card-count">
                {{ assemblies.parts ? assemblies.parts.length : 0 }}
                {{ (assemblies.parts && assemblies.parts.length === 1) ? 'part' : 'parts' }}
              </span>
            </button>

            <div class="assembly-card-body"
                 v-show="isAssemblyExpanded(assemblies.value)">
              <b-table stacked="lg" :fields="fields" :items="assemblies.parts">
                <!-- A virtual column -->
                <template v-slot:cell(item)="data">
                  <div class="nowrap">
                    {{ data.item.item }}
                  </div>
                  <div style="display: none">
                    {{ (data.item.machineId = machineId) }}
                  </div>
                  <div style="display: none">
                    {{ (data.item.assemblyId = assemblyId) }}
                  </div>
                </template>

                <!-- A custom formatted column -->
                <template v-slot:cell(code)="data">
                  <div class="nowrap part-code-cell">
                    <span>{{ data.item.code }}</span>
                    <span class="wear-discount-chip"
                          v-if="wearPartIds[data.item.partId || data.item.id]">
                      2% Discount
                    </span>
                  </div>
                </template>

                <!-- A virtual composite column -->
                <template v-slot:cell(description)="data">
                  <div class="text-left">
                    {{ data.item.description }}
                  </div>
                </template>

                <!-- Optional default data cell scoped slot -->
                <template v-slot:cell(quantityPerMachine)="data">
                  {{ data.item.quantityPerMachine }}
                </template>

                <template v-slot:cell(price)="data">
                  {{ data.item.price | currency }}
                </template>

                <template v-slot:cell(quantity)="data">
                  <button
                    class="btn btn-dark table-btn-icon table-btn-added"
                    @click="scrollTo()"
                    v-show="data.item.show"
                    aria-label="Already added"
                    title="Added">
                    ✓
                  </button>
                  <div style="display: none">{{ data.item.show }}</div>
                  <input
                    type="number"
                    v-model="data.item.quantity"
                    placeholder="1"
                    class="table-input"
                    v-show="!data.item.show"
                  />
                  <button
                    v-show="!data.item.show"
                    class="btn btn-dark table-btn-icon"
                    @click="addToPartsList(data.item);"
                    aria-label="Add part"
                    title="Add part">
                    +
                  </button>
                </template>

                <template v-slot:cell(cost)="data">
                  <div style="display: none">
                    {{
                      (data.item.cost = data.item.price * data.item.quantity)
                    }}
                  </div>
                  <div v-if="data.item.cost">
                    {{ data.item.cost | currency }}
                  </div>
                  <div v-else>{{ data.item.price | currency }}</div>
                </template>
              </b-table>
            </div>
          </div>
            </div>
          </div>
        </div>
      </div>

      <div id="partsList">
        <div class="container">
          <div class="parts-list-actions">
            <button class="btn btn-primary parts-list-actions-btn"
                    @click="goToCart()">
              Go To Cart
            </button>
          </div>
        </div>
      </div>
    </b-form>
  </section>
</template>

<script>
import FeApiService from "@/assets/js/FeApiService";
const apiService = new FeApiService();

import "expose-loader?$!expose-loader?jQuery!jquery";

import Vue from "vue";
import Vue2Filters from "vue2-filters";

Vue.use(Vue2Filters);

export default {
  name: "RFQShopAssembly",
  computed: {
    // Heading adapts to the entry point: a cart "Add Parts" click means the
    // machine is already in the quote cart, so we frame it as adding more
    // parts instead of showing the plain machine name.
    pageTitle() {
      if (this.isFromCart && this.machineName) {
        return "Add more parts to your " + this.machineName;
      }
      return this.machineName || "";
    },

    // Filter the customer's frequently-ordered list to exclude
    // parts already in this session's partsList — once added,
    // the suggestion drops out so the panel stays fresh.
    filteredUpsellParts() {
      var top = (this.insights && this.insights.topParts) || [];
      if (!top.length) return [];

      var staged = {};
      (this.partsList || []).forEach(function (p) {
        var pid = p && (p.partId || p.id);
        if (pid != null) staged[pid] = true;
      });

      return top.filter(function (p) {
        return !staged[p.partId];
      });
    },

    displayedUpsellParts() {
      return this.filteredUpsellParts.slice(0, this.upsellLimit);
    },

    canShowMoreUpsell() {
      return this.filteredUpsellParts.length > this.upsellLimit;
    },

    // ===== STAGING TRAY (cart-derived) =====
    // Flatten fullOrder.purchaseOrders into a per-(part, PO)
    // list. fullOrder is the source of truth — saveCart
    // updates it on every successful add — so this stays in
    // sync with the active quote automatically.
    selectedParts() {
      var arr = [];
      var pos = (this.fullOrder && this.fullOrder.purchaseOrders) || [];
      pos.forEach(function (po) {
        (po.parts || []).forEach(function (p) {
          var code =
            p.code ||
            p.Code ||
            p.partCode ||
            p.PartCode ||
            (p.part && (p.part.code || p.part.partCode)) ||
            "";
          var desc =
            p.description ||
            p.Description ||
            p.partDescription ||
            (p.part && p.part.description) ||
            "";
          arr.push({
            partId: p.id || p.partId || p.Id || p.PartId,
            partCode: code,
            description: desc,
            quantity: p.quantity || p.Quantity || p.quantityPerMachine || 1,
            poNumber: po.number || po.Number,
            _po: po,
            _part: p,
          });
        });
      });
      return arr;
    },

    // Group selectedParts by poNumber → "Quote #N" labels.
    // First PO encountered = Quote #1, etc. Same shape as
    // Create_-_Order's groupedSelections so the markup is
    // shared; only the user-facing label differs (Quote vs
    // Order) which is set in the template.
    groupedSelections() {
      var groups = {};
      var poOrder = [];
      (this.selectedParts || []).forEach(function (p) {
        var po = p.poNumber || "(no ref)";
        if (!groups[po]) {
          groups[po] = { poNumber: po, parts: [] };
          poOrder.push(po);
        }
        groups[po].parts.push(p);
      });
      return poOrder.map(function (po, i) {
        return {
          orderNumber: i + 1,
          poNumber: groups[po].poNumber,
          parts: groups[po].parts,
        };
      });
    },

    selectedPartsCount() {
      return (this.selectedParts || []).length;
    },
  },
  mounted() {
    if (this.$route.params.fullOrder) {
      this.fullOrder = this.$route.params.fullOrder;
    }

    if (this.$route.params.image) {
      this.image = this.$route.params.image;
    }

    if (this.$route.params.partsList) {
      this.partsList = [];
      this.poNumber = this.$route.params.number;
      localStorage.poNumber = this.poNumber;
      this.machineId = this.$route.params.machineId;
      this.assemblyId = this.$route.params.assemblyId;
      this.cartIdRFQ = this.$route.params.id;
      this.configuredWear = this.$route.params.configuredWear;
      this.fullOrder = this.$route.params.fullOrder;
      this.order = this.$route.params.order;

      // Cart-driven entry — flip the heading to "Add more parts to your..."
      this.isFromCart = true;

      this.updateSubtotal(this.partsList);
    }

    this.machineDetails();
    this.getSubAssembly();
    // this.getWearItems();
    this.getAssembly();
    this.loadWearPartIds();
    this.loadUpsellInsights();
  },
  data() {
    return {
      cartIdRFQ: localStorage.cartIdRFQ,
      assemblyId: localStorage.assemblyIdRFQ,
      machineId: localStorage.machineIdRFQ,
      configuredWear: false,
      poNumber: localStorage.poNumberRFQ,
      companyId: localStorage.companyId,
      // True when the user arrived via "Add Parts" from a quote-side cart
      // row — the page heading swaps to "Add more parts to your..." to
      // reflect that the machine is already in the quote cart.
      isFromCart: false,
      order: "",
      machineName: "",
      machineDescription: "",
      editPoNumber: false,
      subTotal: 0,
      subAssemblies: [],
      selectedSubAssemblies: [],
      parts: [],
      partList: [],
      displayParts: [],
      wearParts: [],
      fullOrder: "",
      fields: [
        "item",
        "code",
        "description",
        { key: "quantityPerMachine", label: "QTY/MACH" },
        "price",
        { key: "quantity", label: "Cart Qty" },
        "cost",
      ],
      partItems: [],
      partsFields: [
        "item",
        "code",
        "description",
        { key: "quantityPerMachine", label: "QTY/MACH" },
        "price",
        { key: "quantity", label: "Cart Qty" },
        "cost",
      ],
      partsList: [],
      image: "",
      url: "https://steeleimages.blob.core.windows.net/images/",
      isSubAssemblyLoading:true,
      // Map of PartId → true for parts in the global Wears
      // catalog. Drives the inline "2% Discount" chip next to
      // each part's code in the parts table. Loaded on mount.
      wearPartIds: {},
      // Per-sub-assembly expand state — keyed by
      // assemblies.value (matches the existing :key on the
      // v-for). Default empty so all cards start COLLAPSED.
      expandedAssemblies: {},
      // ===== FREQUENTLY ORDERED UPSELL =====
      insights: { topParts: [] },
      upsellLimit: 5,
      companyName: "",
      addingInsightPart: false,
      // Debounce + concurrency plumbing for the staging tray
      // sync. See ShopAssembly_-_Orders.vue for the full
      // explanation; same shape applies here.
      _syncTimer: null,
      _syncDirty: false,
      // Image zoom modal state — clamped to [0.5, 3.0].
      // Reset to 1 each time the diagram banner is clicked.
      imageZoom: 1,
    };
  },
  methods: {
    // Load the customer's most-ordered parts for the
    // "Frequently Ordered" rail. Same getInsights endpoint
    // the dashboard / create / search pages use. Soft-fail.
    async loadUpsellInsights() {
      var companyId = this.companyId || localStorage.companyId || "";
      if (!companyId) return;
      try {
        var data = await apiService.getInsights({
          companyId: companyId,
          allTime: false,
          take: 50,
        });
        this.insights = data || { topParts: [] };

        if (apiService.getCompanyList) {
          apiService.getCompanyList().then((list) => {
            var match = (list || []).find((c) => {
              return String(c.value) === String(companyId);
            });
            if (match) this.companyName = match.text;
          }).catch(() => { /* ignore */ });
        }
      } catch (e) {
        console.warn("[ShopAssembly] upsell insights load failed:", e);
        this.insights = { topParts: [] };
      }
    },

    // Quick-add an upsell part: route to /search with the
    // part code pre-filled. Search has the full add-to-cart
    // flow and can resolve any part's machine + assembly +
    // PO context — including for quote destinations via the
    // ?from=quote URL flag (Search reads that and stages
    // toward Quotes). We pass that here so the Search page
    // pre-locks the destination type to Quote.
    openUpsellPart(part) {
      if (!part) return;
      this.$router.push({
        name: "Search",
        query: {
          code: part.partCode,
          allParts: "true",
          from: "quote",
        },
      });
    },

    // ===== IMAGE ZOOM CONTROLS =====
    zoomIn() {
      this.imageZoom = Math.min(3, this.imageZoom + 0.25);
    },
    zoomOut() {
      this.imageZoom = Math.max(0.5, this.imageZoom - 0.25);
    },
    resetImageZoom() {
      this.imageZoom = 1;
    },

    // ===== STAGING-TRAY MUTATIONS =====
    // Remove one (part, PO) pairing from the active quote.
    // See ShopAssembly_-_Orders.vue removeQueuedPart for the
    // full state-sync rationale (fullOrder + partsList +
    // table show flags + server cart all updated together).
    removeQueuedPart(p) {
      if (!p || !this.fullOrder) return;
      var pos = this.fullOrder.purchaseOrders || [];
      var poIdx = pos.indexOf(p._po);
      if (poIdx < 0) return;
      var po = pos[poIdx];
      var partIdx = (po.parts || []).indexOf(p._part);
      if (partIdx < 0) return;
      po.parts.splice(partIdx, 1);
      if (po.parts.length === 0) {
        pos.splice(poIdx, 1);
      }
      // Filter from partsList — addToCart rebuilds fullOrder
      // from partsList every click, so any part still in
      // partsList gets resurrected on the next ADD.
      this.partsList = (this.partsList || []).filter(function (pl) {
        return pl.id !== p.partId;
      });
      // Flip the parts table back to "+" for this part.
      this.unmarkAddedInTable(p.partId);
      this.scheduleSyncToCart();
    },

    // Clear all parts from the active quote.
    clearSelectedParts() {
      if (!this.fullOrder) return;
      this.fullOrder.purchaseOrders = [];
      // Wipe partsList — see removeQueuedPart for why this
      // matters. Without it, the next ADD click resurrects
      // every previously-added part.
      this.partsList = [];
      // Reset every part's "added" flag so the parts table
      // shows qty input + "+" on every row again.
      var self = this;
      (this.subAssemblies || []).forEach(function (sa) {
        (sa.parts || []).forEach(function (pt) {
          self.$set(pt, "show", false);
        });
      });
      this.scheduleSyncToCart();
    },

    // Inverse of updateBtn — flips show=false on parts in
    // subAssemblies that match the partId being removed,
    // so the b-table re-renders the "+" controls instead
    // of the ✓ "added" icon.
    unmarkAddedInTable(partId) {
      if (partId == null) return;
      var self = this;
      (this.subAssemblies || []).forEach(function (sa) {
        (sa.parts || []).forEach(function (pt) {
          if (pt.id === partId) {
            self.$set(pt, "show", false);
          }
        });
      });
    },

    // Debounce wrapper — coalesces rapid mutations into one
    // round-trip 300ms after the last change.
    scheduleSyncToCart() {
      if (this._syncTimer) {
        clearTimeout(this._syncTimer);
      }
      var self = this;
      this._syncTimer = setTimeout(function () {
        self._syncTimer = null;
        self.syncQueueToCart();
      }, 300);
    },

    // Push fullOrder.purchaseOrders state to the server.
    // Empty cart → deleteCart (resets local quote state).
    // Non-empty → saveCart with CreateQuote: true so the
    // backend persists this as a quote rather than an order.
    async syncQueueToCart() {
      if (this.addingInsightPart) {
        this._syncDirty = true;
        return;
      }
      if (!this.cartIdRFQ || String(this.cartIdRFQ) === "0") {
        return;
      }
      this.addingInsightPart = true;

      try {
        var pos = (this.fullOrder && this.fullOrder.purchaseOrders) || [];

        if (pos.length === 0) {
          try {
            await apiService.deleteCart({ id: parseInt(this.cartIdRFQ, 10) });
          } catch (e) {
            console.warn("[ShopAssembly RFQ] deleteCart failed (possibly already gone):", e);
          }
          localStorage.cartIdRFQ = 0;
          this.cartIdRFQ = 0;
          this.fullOrder = "";
          if (this.$cartBus) {
            this.$cartBus.$emit("cart-updated");
          }
          return;
        }

        var resp = await apiService.saveCart({
          id: this.cartIdRFQ || 0,
          number: this.poNumber,
          purchaseOrders: pos,
          machineId: this.machineId,
          assemblyId: this.assemblyId,
          companyId: this.companyId,
          CreateQuote: true,
        });
        if (resp && resp.purchaseOrders) {
          this.fullOrder.purchaseOrders = resp.purchaseOrders;
        }
        if (resp && resp.id) {
          this.cartIdRFQ = resp.id;
          localStorage.cartIdRFQ = resp.id;
        }
        if (this.$cartBus) {
          this.$cartBus.$emit("cart-updated");
        }
      } catch (e) {
        console.warn("[ShopAssembly RFQ] syncQueueToCart failed:", e);
      } finally {
        this.addingInsightPart = false;
        if (this._syncDirty) {
          this._syncDirty = false;
          this.scheduleSyncToCart();
        }
      }
    },

    // Pull THIS company's wear-eligible PartIds (its dbo.Wears
    // = the Account Setup wear list) for the inline "2% Discount"
    // chip in the parts table. The chip must reflect the
    // customer's OWN wear parts, not the global wear-eligible
    // catalog — so use the company-scoped endpoint keyed by
    // localStorage.companyId. Defensive against missing API
    // method, missing companyId, bad response shape, network
    // failure (or a pre-deploy 404 on the new endpoint) — all
    // paths fall back to {} (no chips, no crash).
    loadWearPartIds() {
      if (!apiService.getCompanyWearPartIds) {
        console.warn("[ShopAssembly] loadWearPartIds skipped — apiService.getCompanyWearPartIds missing");
        this.wearPartIds = {};
        return;
      }
      var companyId = localStorage.companyId || "";
      if (!companyId) {
        console.warn("[ShopAssembly] loadWearPartIds skipped — no companyId yet");
        this.wearPartIds = {};
        return;
      }
      var self = this;
      apiService
        .getCompanyWearPartIds(companyId)
        .then(function (data) {
          var map = {};
          if (Array.isArray(data)) {
            data.forEach(function (item) {
              if (item == null) return;
              var id;
              if (typeof item === "number" || typeof item === "string") {
                id = item;
              } else if (typeof item === "object") {
                id = item.partId || item.PartId || item.id || item.Id;
              }
              if (id != null) map[id] = true;
            });
          } else {
            console.warn("[ShopAssembly] getCompanyWearPartIds returned non-array:", data);
          }
          self.wearPartIds = map;
          console.log("[ShopAssembly] loaded", Object.keys(map).length, "wear part ids");
        })
        .catch(function (err) {
          console.warn("[ShopAssembly] could not load wear part ids:", err);
          self.wearPartIds = {};
        });
    },

    // Accordion helpers — flip per-sub-assembly expand state
    // so the parts table only renders when the user clicks
    // the card header.
    isAssemblyExpanded(key) {
      return !!this.expandedAssemblies[key];
    },
    toggleAssembly(key) {
      var current = !!this.expandedAssemblies[key];
      this.$set(this.expandedAssemblies, key, !current);
    },

    getSubAssembly() {
      apiService
        .getAssemblyWithParts({
          id: this.assemblyId,
        })
        .then((request) => {
          this.selectItemsSuccessful(request);
        })
        .catch((err) => this.selectItemsFailed(err));
    },
    selectItemsSuccessful(request) {
      this.subAssemblies = request;

      this.subAssemblies.forEach((item) => {
        item.parts.forEach((part) => {
          part.quantity = part.quantityPerMachine;
        });

        this.showAssemblyParts(item.value);
        this.partList.push(item.value);
      });

      // this.getWareItems();
    },
    selectItemsFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    getAssembly() {
      apiService
        .getAssemblyDetails({
          id: this.assemblyId,
        })
        .then((request) => {
          this.getAssemblySuccessful(request);
        })
        .catch((err) => this.getAssemblyFailed(err));
    },
    getAssemblySuccessful(request) {
      if (request.imagePath != null) {
        this.image = this.url + request.imagePath;
      } else {
        this.image = null;
      }
    },
    getAssemblyFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    getParts(id) {
      apiService
        .getPartsByAssembly({
          id: id,
        })
        .then((request) => {
          this.selectPartsItemsSuccessful(request);
        })
        .catch((err) => this.selectItemsFailed(err));
    },
    selectPartsItemsSuccessful(request) {
      this.partItems = request;
    },
    machineDetails() {
      apiService
        .getCompanyMachineDetails({ id: this.machineId })
        .then((request) => this.machineSuccessful(request))
        .catch((err) => this.machineFailed(err));
    },
    machineSuccessful(request) {
      (this.machineName = request.code),
        (this.machineDescription = request.description);
    },
    machineFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    changePoNumber(num) {
      if(num) {
        localStorage.poNumberRFQ = num;
  
        this.editPoNumber = !this.editPoNumber;
      } else {
        this.$bvToast.toast("Please enter a valid Quote Reference to continue", {
          title: "Warning! Quote Reference field cannot be empty",
          autoHideDelay: 5000,
          appendToast: false,
          variant: "danger",
          solid: true
      });
      }
    },
    showAssemblyParts(assembly) {
      this.selectedSubAssemblies.push(assembly);
      this.isSubAssemblyLoading = false
      //  console.log(this.selectedSubAssemblies);
      $("[data-id='" + assembly + "']").show();
    },
    showParts(assembly) {
      if (event.target.tagName === "INPUT") {
        if (event.target.checked) {
          // Checkbox is being checked
          // the value that will be set is event.target._value

          this.selectedSubAssemblies.push(assembly);
          $("[data-id='" + assembly + "']").show();
        } else {
          // Checkbox is being un-checked
          // the value that will be unset is set is event.target._value
          this.selectedSubAssemblies.splice(
            this.selectedSubAssemblies.indexOf(assembly),
            1
          );
          $("[data-id='" + assembly + "']").hide();
        }
      }
    },

    // getWearItems() {
    //   apiService
    //     .getWearItems({
    //       id: this.assemblyId
    //     })
    //     .then(request => {
    //       this.wareItemsSuccessful(request);
    //     })
    //     .catch(err => this.wareItemsFailed(err));
    // },
    // wareItemsSuccessful(request) {
    //   this.wearParts = request;

    //   this.configuredWear = localStorage.configuredWear;
    // },
    // wareItemsFailed(err) {
    //   this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
    //       title: err.error.response.data.title,
    //       autoHideDelay: 10000,
    //       appendToast: false,
    //       variant: "danger",
    //       solid: true
    //   });
    // },

    addToPartsList(part) {
      this.partsList = [];
      if (!part.quantity) {
        part.quantity = part.quantityPerMachine;
        part.cost = part.price * part.quantity;
      }

      var idx = $.inArray(part, this.partsList);
      if (idx == -1) {
        this.partsList.push(part);
      } else {
        for (var i in this.partsList) {
          if (this.partsList[i].id == part.id) {
            this.partsList[i].quantity = part.qty;
            break; //Stop this loop, we found it!
          }
        }
      }

      this.addToCart(part);
      this.updateSubtotal(this.partsList);

      //  if (this.fullOrder.purchaseOrders) {
      //     for (var p in this.fullOrder.purchaseOrders) {
      //       if (this.fullOrder.purchaseOrders[p].number == this.poNumber
      //           && part.machineId == this.fullOrder.purchaseOrders[p].machineId
      //           && part.assemblyId == this.fullOrder.purchaseOrders[p].assemblyId) {

      //         this.fullOrder.purchaseOrders[p].parts.push(part);

      //       }
      //     }
      //   }
    },
    addToCart(part) {
      this.cartIdRFQ = localStorage.cartIdRFQ || this.cartIdRFQ;
      if (this.fullOrder) {
        this.fullOrder.purchaseOrders = [];
        var po = this.poNumber;
        var assemblyID = this.assemblyId;
        if (
          this.fullOrder.purchaseOrders.filter(function(e) {
            return e.number == po;
          }).length > 0
        ) {
          if (
            this.fullOrder.purchaseOrders.filter(function(e) {
              return e.assemblyId == assemblyID;
            }).length > 0
          ) {
            // nothing
            for (let i in this.partsList) {
              newPO = {
                assemblyId: this.partsList[i].assemblyId || this.assemblyId,
                machineId: this.machineId,
                parts: [this.partsList[i]],
                Number: this.poNumber,
                configuredWear: true,
              };

              this.fullOrder.purchaseOrders.push(newPO);
            }
          } else {
            var newPO = {
              active: true,
              assemblyId: this.assemblyId,
              machineId: this.machineId,
              number: this.poNumber,
              parts: this.partsList,
              cartId: this.cartIdRFQ,
              Number: this.poNumber,
              configuredWear: this.configuredWear,
            };

            this.fullOrder.purchaseOrders.push(newPO);
          }
          apiService
            .saveCart({
              id: this.cartIdRFQ || 0,
              number: this.poNumber,
              machineId: this.machineId,
              purchaseOrders: this.fullOrder.purchaseOrders,
              companyId: this.companyId,
              assemblyId: this.assemblyId, 
              CreateQuote: true,
            })
            .then((request) => {
              this.saveCartSuccessful(request);
              this.updateBtn(part);
            })
            .catch((err) => this.fail(err));
        } else {
          newPO = {
            active: true,
            assemblyId: this.assemblyId,
            machineId: this.machineId,
            number: this.poNumber,
            parts: this.partsList,
            cartId: this.cartIdRFQ,
            Number: this.poNumber,
            configuredWear: this.configuredWear,
          };

          this.fullOrder.purchaseOrders.push(newPO);
          apiService
            .saveCart({
              id: this.cartIdRFQ || 0,
              number: this.poNumber,
              machineId: this.machineId,
              purchaseOrders: this.fullOrder.purchaseOrders,
              companyId: this.companyId,
              CreateQuote: true,
            })
            .then((request) => {
              this.saveCartSuccessful(request);
              this.updateBtn(part);
            })
            .catch((err) => this.fail(err));
        }
      } else {
        this.fullOrder = {
          purchaseOrders: [],
        };

        for (let i in this.partsList) {
          newPO = {
            assemblyId: this.partsList[i].assemblyId,
            machineId: this.machineId,
            parts: [this.partsList[i]],
            Number: this.poNumber,
            configuredWear: true,
          };

          this.fullOrder.purchaseOrders.push(newPO);
        }
        apiService
            .saveCart({
              id: this.cartIdRFQ || 0,
              number: this.poNumber,
              machineId: this.machineId,
              purchaseOrders: this.fullOrder.purchaseOrders,
              companyId: this.companyId,
              CreateQuote: true,
            })
            .then((request) => {
              this.saveCartSuccessful(request);
              this.updateBtn(part);
            })
            .catch((err) => this.fail(err));
      }
    },
    saveCartSuccessful(request) {
      localStorage.cartIdRFQ = request.id;
      this.cartIdRFQ = request.id;
      this.fullOrder.purchaseOrders = request.purchaseOrders;
      if (this.$cartBus) {
        this.$cartBus.$emit("cart-updated");
      }
    },
    fail(err) {
      this.$bvToast.toast(err.error.response.data.traceId ? "TraceID: " + err.error.response.data.traceId : err.error.response.data, {
          title: err.error.response.data.title,
          autoHideDelay: 10000,
          appendToast: false,
          variant: "danger",
          solid: true,
        });
    },
    updateBtn(part) {
      this.subAssemblies.forEach(machine => {
      const partToUpdate = machine.parts.find(p => p.id === part.id);
      if (partToUpdate) {
        this.$set(partToUpdate, 'show', true);
      }
    });
    },
    scrollTo() {
      $("html, body").animate(
        {
          scrollTop: $("#partsList").offset().top,
        },
        500
      );
    },
    removeItem(item) {
      this.subTotal = this.subTotal - item.cost;

      var idx = $.inArray(item, this.partsList);
      this.partsList.splice(idx, 1);
      item.show = false;
    },
    removeAllParts() {
      for (var i in this.partsList) {
        this.partsList[i].show = false;
      }
      this.partsList = [];
      this.subTotal = 0;
    },
    goToCart() {
      this.$router.push("/cart/rfqsaveditems");
    },
    updateSubtotal(list) {
      this.subTotal = 0;

      for (var i in list) {
        var cost = list[i].price * list[i].quantity;

        this.subTotal = this.subTotal + cost;
        // console.log(this.subTotal);
      }
    },
  },
};
</script>

<style scoped>
/* ===== PAGE HEADING + PO BADGE ===== */
.page-heading-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 8px;
}

.page-heading-row h1 {
  margin: 0;
  flex: 1 1 auto;
  min-width: 240px;
}

.page-heading-po {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-left: 3px solid #c0392b;
  border-radius: 6px;
  padding: 8px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.po-badge-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #777;
}

.po-badge-value {
  font-size: 15px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
}

@media (max-width: 767.98px) {
  .page-heading-row {
    flex-direction: column;
    align-items: flex-start;
  }
  .page-heading-po {
    width: 100%;
    justify-content: space-between;
  }
}

/* ===== ASSEMBLY ACCORDION CARDS ===== */
.assembly-card {
  background: #fff;
  border: 1px solid #ececec;
  border-top: 3px solid #c0392b;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: 16px;
  overflow: hidden;
}

.assembly-card-header {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  background: transparent;
  border: 0;
  padding: 14px 18px;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.15s ease;
}

.assembly-card-header:hover {
  background: #faf5f4;
}

.assembly-card-header:focus {
  outline: none;
  background: #faf5f4;
  box-shadow: inset 0 0 0 2px rgba(192, 57, 43, 0.15);
}

.assembly-card-header.is-open {
  border-bottom: 1px solid #ececec;
}

.assembly-card-chev {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  background: #c0392b;
  color: #fff;
  border-radius: 50%;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  font-family: monospace;
}

.assembly-card-titles {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.assembly-card-name {
  font-size: 15px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.2px;
}

.assembly-card-machine {
  font-size: 12px;
  color: #888;
  font-weight: 500;
}

.assembly-card-count {
  font-size: 12px;
  color: #555;
  font-weight: 600;
  background: #f3f3f3;
  padding: 4px 10px;
  border-radius: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.assembly-card-body {
  padding: 8px 18px 16px 18px;
}

.assembly-card-body .b-table,
.assembly-card-body .table {
  margin-bottom: 0;
}

/* ===== WEAR DISCOUNT CHIP (in-table) ===== */
.part-code-cell {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.wear-discount-chip {
  display: inline-block;
  padding: 1px 6px;
  background: #e8f5e9;
  border: 1px solid #2e7d32;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #2e7d32;
  white-space: nowrap;
  line-height: 1.4;
}

/* ===== TWO-COLUMN BODY LAYOUT =====
   Same as ShopAssembly Orders side. */
.assembly-body-wrap {
  margin-top: 12px;
}

.assembly-top-right-col {
  display: flex;
  flex-direction: column;
}

.assembly-top-right-col > .upsell-panel {
  flex: 1 1 auto;
}

/* ===== DIAGRAM BANNER =====
   Full-width clickable bar at the top of the parts column,
   matching ShopAssembly_-_Orders.vue's diagram-banner. */
.diagram-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  margin: 0 0 14px 0;
  background: #fff;
  border: 1px solid #ececec;
  border-left: 3px solid #c0392b;
  border-radius: 6px;
  color: #c0392b;
  font-size: 13px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.diagram-banner:hover,
.diagram-banner:focus {
  background: #fff5f3;
  border-color: #c0392b;
  outline: none;
}

.diagram-banner-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  background: #c0392b;
  color: #fff;
  border-radius: 4px;
}

.diagram-banner-label {
  flex: 1 1 auto;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  font-size: 12px;
  color: #2b2b2b;
}

.diagram-banner-cta {
  flex-shrink: 0;
  font-size: 11px;
  color: #c0392b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ===== ASSEMBLY DIAGRAM MODAL ===== */
.modal-image-zoom .modal-content {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.18);
}

.image-zoom-header {
  padding: 12px 18px;
  border-bottom: 1px solid #ececec;
  background: #fff;
}

.image-zoom-header .modal-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #2b2b2b;
  margin: 0;
}

.image-zoom-body {
  padding: 0;
  background: #fafafa;
  overflow: auto;
  max-height: 65vh;
}

.image-zoom-canvas {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 360px;
  padding: 16px;
}

.image-zoom-img {
  display: block;
  max-width: 100%;
  height: auto;
  transition: transform 0.18s ease;
  transform-origin: center center;
}

.image-zoom-footer {
  display: flex;
  padding: 0;
  margin: 0;
  border-top: 1px solid #ececec;
  background: #fff;
}

.image-zoom-btn {
  flex: 1 1 0;
  padding: 14px 12px;
  background: #fff;
  border: none;
  border-right: 1px solid #ececec;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #2b2b2b;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.image-zoom-btn:hover:not(:disabled) {
  background: #f5f5f5;
}

.image-zoom-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.image-zoom-btn:last-child {
  border-right: none;
}

.image-zoom-btn-close {
  background: #c0392b;
  color: #fff;
}

.image-zoom-btn-close:hover {
  background: #a93222;
  color: #fff;
}

/* ===== GO TO CART ACTION BAR ===== */
.parts-list-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding: 0 4px;
}

.parts-list-actions-btn {
  min-width: 160px;
}

/* ===== TIGHT PARTS TABLE ===== */
.assembly-card-body {
  padding: 6px 12px 12px 12px;
}

.assembly-card-body .table {
  font-size: 11.5px;
  table-layout: auto;
}

.assembly-card-body .table th,
.assembly-card-body .table td {
  padding: 6px 5px;
  font-size: 11.5px;
  vertical-align: middle;
}

.assembly-card-body .table th {
  font-size: 9.5px;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.assembly-card-body .table th:nth-child(1),
.assembly-card-body .table td:nth-child(1),
.assembly-card-body .table th:nth-child(4),
.assembly-card-body .table td:nth-child(4),
.assembly-card-body .table th:nth-child(5),
.assembly-card-body .table td:nth-child(5),
.assembly-card-body .table th:nth-child(7),
.assembly-card-body .table td:nth-child(7) {
  width: 1px;
  white-space: nowrap;
}

/* Cart Qty column — input + + must stay on one line. */
.assembly-card-body .table th:nth-child(6),
.assembly-card-body .table td:nth-child(6) {
  white-space: nowrap;
  min-width: 86px;
  text-align: center;
}

.assembly-card-body .table .table-input {
  display: inline-block;
  vertical-align: middle;
  width: 38px;
  padding: 3px 4px;
  font-size: 11px;
  text-align: center;
  margin: 0;
}

/* ===== ICON BUTTONS (Add / Added) ===== */
.assembly-card-body .table .table-btn-icon {
  display: inline-flex;
  vertical-align: middle;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  margin-left: 4px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  border-radius: 4px;
}

.assembly-card-body .table .table-btn-icon.table-btn-added {
  background: #777;
  border-color: #777;
  color: #fff;
  cursor: default;
}

.assembly-card-body .table .part-code-cell {
  font-size: 11.5px;
}

.assembly-side-col > .insights-selected-tray,
.assembly-side-col > .upsell-panel {
  margin-bottom: 16px;
}

.assembly-side-col > :last-child {
  margin-bottom: 0;
}

@media (min-width: 992px) {
  .assembly-side-col {
    position: sticky;
    top: 16px;
    align-self: flex-start;
  }
}

/* ===== STAGING TRAY (cart-derived, grouped by PO) =====
   Same visual treatment as Create_-_Order so users see a
   coherent staging UX across the create / shop flows. */
.insights-selected-tray {
  margin-bottom: 14px;
  padding: 10px 12px;
  background: #fffaf0;
  border: 1px solid #f3e3b8;
  border-radius: 6px;
}

.insights-selected-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.insights-selected-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #8a6d2a;
}

.insights-selected-clear {
  background: transparent;
  border: none;
  padding: 0;
  font-size: 11px;
  color: #888;
  text-decoration: underline;
  cursor: pointer;
}

.insights-selected-clear:hover {
  color: #c0392b;
}

.insights-order-group {
  margin-top: 10px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #ecdfb3;
  border-radius: 4px;
}

.insights-order-group:first-of-type {
  margin-top: 4px;
}

.insights-order-group-head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 6px;
  padding-bottom: 5px;
  border-bottom: 1px dashed #f0e3b5;
}

.insights-order-group-num {
  font-size: 12px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.insights-order-group-meta {
  font-size: 11px;
  color: #8a6d2a;
}

.insights-order-group-meta strong {
  color: #5a4716;
}

.insights-queue-rows {
  list-style: none;
  margin: 0;
  padding: 0;
}

.insights-queue-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid #f5e9c5;
}

.insights-queue-row:last-child {
  border-bottom: none;
}

.insights-queue-row-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.insights-queue-row-code {
  font-size: 12px;
  font-weight: 700;
  color: #5a4716;
  letter-spacing: 0.2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.insights-queue-row-desc {
  font-size: 10px;
  color: #8a6d2a;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.insights-queue-row-qty {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.insights-qty-input {
  width: 56px;
  padding: 3px 6px;
  font-size: 12px;
  font-weight: 600;
  color: #222;
  text-align: center;
  border: 1px solid #d4c486;
  border-radius: 4px;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.insights-qty-input:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.15);
}

.insights-qty-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #8a6d2a;
}

.insights-queue-row-remove {
  flex-shrink: 0;
  background: transparent;
  border: none;
  width: 22px;
  height: 22px;
  padding: 0;
  font-size: 16px;
  font-weight: 700;
  color: #888;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.insights-queue-row-remove:hover {
  background: #c0392b;
  color: #fff;
}

.insights-queue-row-remove:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ===== FREQUENTLY ORDERED UPSELL PANEL ===== */
.upsell-panel {
  background: #fff;
  border: 1px solid #ececec;
  border-top: 3px solid #c0392b;
  border-radius: 6px;
  padding: 16px 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.upsell-header {
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #d8d8d8;
}

.upsell-title {
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.upsell-sub {
  margin: 0;
  font-size: 12px;
  color: #777;
  line-height: 1.4;
}

.upsell-sub strong {
  color: #444;
}

.upsell-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.upsell-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.upsell-item:last-child {
  border-bottom: none;
}

.upsell-item-main {
  flex: 1 1 auto;
  min-width: 0;
}

.upsell-item-code {
  font-size: 13px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.2px;
  margin-bottom: 2px;
}

.upsell-item-desc {
  font-size: 13px;
  color: #333;
  line-height: 1.35;
  margin-bottom: 4px;
}

.upsell-item-meta {
  font-size: 11px;
  color: #888;
  font-style: italic;
}

.upsell-item-meta strong {
  color: #555;
  font-style: normal;
}

.upsell-item-add {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #c0392b;
  background: #fff;
  border: 1.5px solid #c0392b;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.upsell-item-add:hover {
  background: #c0392b;
  color: #fff;
}

.upsell-item-add:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.upsell-see-more {
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px dashed #e5e5e5;
  text-align: center;
}

.upsell-see-more-btn {
  background: transparent;
  border: 1px solid #c0392b;
  color: #c0392b;
  padding: 5px 14px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border-radius: 4px;
  cursor: pointer;
}

.upsell-see-more-btn:hover {
  background: #c0392b;
  color: #fff;
}
</style>